import { useEffect, useRef, useCallback, useState } from 'react';

interface UseAccessibilityOptions {
  trapFocus?: boolean;
  announceChanges?: boolean;
  reducedMotion?: boolean;
  highContrast?: boolean;
}

export const useAccessibility = (options: UseAccessibilityOptions = {}) => {
  const {
    trapFocus = false,
    announceChanges = true,
    reducedMotion: forcedReducedMotion,
    highContrast: forcedHighContrast
  } = options;

  const [preferences, setPreferences] = useState({
    reducedMotion: false,
    highContrast: false,
    prefersColorScheme: 'light' as 'light' | 'dark',
    screenReaderActive: false
  });

  const announcementRef = useRef<HTMLDivElement | null>(null);
  const focusTrapRef = useRef<HTMLElement | null>(null);

  // Detect user preferences
  useEffect(() => {
    const detectPreferences = () => {
      setPreferences(prev => ({
        ...prev,
        reducedMotion: forcedReducedMotion ?? window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        highContrast: forcedHighContrast ?? window.matchMedia('(prefers-contrast: high)').matches,
        prefersColorScheme: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
        // Detect screen reader by checking for reduced motion + no hover capability
        screenReaderActive: window.matchMedia('(prefers-reduced-motion: reduce)').matches && 
                           window.matchMedia('(hover: none)').matches
      }));
    };

    detectPreferences();

    // Listen for preference changes
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const contrastQuery = window.matchMedia('(prefers-contrast: high)');
    const colorSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');

    motionQuery.addEventListener('change', detectPreferences);
    contrastQuery.addEventListener('change', detectPreferences);
    colorSchemeQuery.addEventListener('change', detectPreferences);

    return () => {
      motionQuery.removeEventListener('change', detectPreferences);
      contrastQuery.removeEventListener('change', detectPreferences);
      colorSchemeQuery.removeEventListener('change', detectPreferences);
    };
  }, [forcedReducedMotion, forcedHighContrast]);

  // Create announcement element for screen readers
  useEffect(() => {
    if (announceChanges && !announcementRef.current) {
      const announcer = document.createElement('div');
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.setAttribute('class', 'sr-only');
      announcer.style.cssText = `
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
      `;
      document.body.appendChild(announcer);
      announcementRef.current = announcer;
    }

    return () => {
      if (announcementRef.current) {
        document.body.removeChild(announcementRef.current);
        announcementRef.current = null;
      }
    };
  }, [announceChanges]);

  // Focus trap implementation
  useEffect(() => {
    if (!trapFocus || !focusTrapRef.current) return;

    const container = focusTrapRef.current;
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), [contenteditable]'
    ) as NodeListOf<HTMLElement>;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);
    firstElement?.focus();

    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  }, [trapFocus]);

  // Announce changes to screen readers
  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (!announceChanges || !announcementRef.current) return;

    announcementRef.current.setAttribute('aria-live', priority);
    announcementRef.current.textContent = message;

    // Clear the message after announcement to allow repeated announcements
    setTimeout(() => {
      if (announcementRef.current) {
        announcementRef.current.textContent = '';
      }
    }, 1000);
  }, [announceChanges]);

  // Focus management utilities
  const focusFirst = useCallback((container?: HTMLElement) => {
    const target = container || focusTrapRef.current || document.body;
    const firstFocusable = target.querySelector(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ) as HTMLElement;
    
    firstFocusable?.focus();
  }, []);

  const focusLast = useCallback((container?: HTMLElement) => {
    const target = container || focusTrapRef.current || document.body;
    const focusableElements = target.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ) as NodeListOf<HTMLElement>;
    
    const lastElement = focusableElements[focusableElements.length - 1];
    lastElement?.focus();
  }, []);

  // Keyboard navigation helper
  const handleKeyboardNavigation = useCallback((
    event: KeyboardEvent,
    handlers: {
      onEscape?: () => void;
      onEnter?: () => void;
      onArrowUp?: () => void;
      onArrowDown?: () => void;
      onArrowLeft?: () => void;
      onArrowRight?: () => void;
      onHome?: () => void;
      onEnd?: () => void;
    } = {}
  ) => {
    const {
      onEscape,
      onEnter,
      onArrowUp,
      onArrowDown,
      onArrowLeft,
      onArrowRight,
      onHome,
      onEnd
    } = handlers;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        onEscape?.();
        break;
      case 'Enter':
      case ' ': // Space
        if (event.target === event.currentTarget) {
          event.preventDefault();
          onEnter?.();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        onArrowUp?.();
        break;
      case 'ArrowDown':
        event.preventDefault();
        onArrowDown?.();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        onArrowLeft?.();
        break;
      case 'ArrowRight':
        event.preventDefault();
        onArrowRight?.();
        break;
      case 'Home':
        event.preventDefault();
        onHome?.();
        break;
      case 'End':
        event.preventDefault();
        onEnd?.();
        break;
    }
  }, []);

  // ARIA helpers
  const getAriaProps = useCallback((type: 'button' | 'menu' | 'dialog' | 'listbox' = 'button', options: {
    expanded?: boolean;
    selected?: boolean;
    disabled?: boolean;
    describedBy?: string;
    labelledBy?: string;
    label?: string;
  } = {}) => {
    const { expanded, selected, disabled, describedBy, labelledBy, label } = options;
    
    const baseProps: Record<string, any> = {
      role: type,
    };

    if (label) baseProps['aria-label'] = label;
    if (labelledBy) baseProps['aria-labelledby'] = labelledBy;
    if (describedBy) baseProps['aria-describedby'] = describedBy;
    if (disabled !== undefined) baseProps['aria-disabled'] = disabled;
    if (expanded !== undefined) baseProps['aria-expanded'] = expanded;
    if (selected !== undefined) baseProps['aria-selected'] = selected;

    return baseProps;
  }, []);

  return {
    preferences,
    announce,
    focusFirst,
    focusLast,
    focusTrapRef,
    handleKeyboardNavigation,
    getAriaProps,
    
    // Utility classes based on preferences
    getAccessibilityClasses: () => ({
      reducedMotion: preferences.reducedMotion,
      highContrast: preferences.highContrast,
      screenReader: preferences.screenReaderActive
    })
  };
};